import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  Heart, 
  Shield, 
  AlertTriangle,
  CheckCircle,
  Clock,
  Activity,
  Phone,
  MapPin,
  Award,
  Zap,
  Calendar,
  Stethoscope,
  Target,
  TrendingUp,
  Scissors,
  Pill,
  Bandage,
  Timer,
  Search,
  FileText,
  Microscope,
  FlaskConical,
  Droplets,
  TestTube,
  Scan,
  Brain,
  Camera,
  Monitor,
  Eye,
  Sparkles,
  CircleDot,
  Users,
  Home,
  XCircle,
  Utensils,
  Package,
  UserCheck,
  Layers,
  Settings,
  PlusCircle,
  Thermometer,
  Siren,
  Truck,
  Leaf,
  Bug,
  Gauge,
  Smile,
  Frown,
  Wind,
  Star,
  Navigation,
  Info,
  ArrowRight
} from 'lucide-react';
import { hospitalInfo } from '../mock';
import { useBusinessInfo } from '../hooks/useBusinessInfo';
import InnerPageLayout from '../components/InnerPageLayout';

const UrgentCare = () => {
  const { businessInfo: currentBusinessInfo } = useBusinessInfo();
  const [urgentCareHours, setUrgentCareHours] = useState(null);
  const [loading, setLoading] = useState(true);

  const API_BASE_URL = process.env.REACT_APP_BACKEND_URL || (window.location.origin.includes("localhost") ? "http://localhost:8000" : window.location.origin + "/pandvah-backend");

  const primaryColor = 'var(--theme-primary)';
  const primaryLight = 'var(--theme-secondary)';
  const primaryBg = 'var(--theme-primary-light)';
  const urgentColor = '#dc2626';

  // Helper function to format time from 24-hour to 12-hour
  const formatTime = (time) => {
    if (!time) return '';
    const [hours, minutes] = time.split(':');
    const hour12 = parseInt(hours) % 12 || 12;
    const ampm = parseInt(hours) >= 12 ? 'PM' : 'AM';
    return `${hour12}:${minutes} ${ampm}`;
  };

  // Helper function to format day hours
  const formatDayHours = (dayData) => {
    if (!dayData || !dayData.is_open) {
      return 'Closed';
    }
    return `${formatTime(dayData.open_time)} - ${formatTime(dayData.close_time)}`;
  };

  useEffect(() => {
    const fetchUrgentCareHours = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/api/urgent-care-hours`);
        if (response.ok) {
          const data = await response.json();
          setUrgentCareHours(data);
        }
      } catch (error) {
        console.error('Error fetching urgent care hours:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchUrgentCareHours();
  }, []);

  const petIllnesses = [
    {
      service: "Diarrhea & Vomiting",
      description: "We quickly diagnose and treat pet GI issues like vomiting and diarrhea—bringing relief to your pet and peace of mind to you",
      icon: Droplets,
      color: "#ef4444"
    },
    {
      service: "Loss of Appetite or Hydration Issues",
      description: "If your pet won't eat or drink, it may signal a serious issue. Our team provides diagnosis and supportive therapies like IV fluids to get them back on track",
      icon: Utensils,
      color: "#f59e0b"
    },
    {
      service: "Dehydration/Excessive Drinking",
      description: "From IV fluids for dehydration to diagnosing excessive drinking, we provide treatments to restore your pet's fluid balance",
      icon: Droplets,
      color: "#0ea5e9"
    },
    {
      service: "Poison or Toxin Exposure",
      description: "Rapid treatment including safe induced vomiting and detox protocols",
      icon: AlertTriangle,
      color: "#dc2626"
    },
    {
      service: "Skin Allergies, Itching, Rashes, Hot Spots",
      description: "Skin allergies cause discomfort for pets. Our team diagnoses and treats itching, rashes, and hot spots to provide fast relief",
      icon: Bug,
      color: "#10b981"
    },
    {
      service: "Upper Respiratory Infections",
      description: "Our team will diagnose the issue and recommend the best treatment to help your pet breathe easier and recover quickly",
      icon: Wind,
      color: "#6366f1"
    },
    {
      service: "Coughing & Sneezing",
      description: "Diagnostics and treatment plans tailored to breed and symptoms for persistent coughing and sneezing episodes",
      icon: Activity,
      color: "#8b5cf6"
    },
    {
      service: "Urinary Concerns",
      description: "Evaluation and care for straining, blood, or frequent attempts",
      icon: CircleDot,
      color: "#a855f7"
    },
    {
      service: "Ear Infections",
      description: "Comprehensive ear cleaning, testing, and medication to end irritation",
      icon: Stethoscope,
      color: "#ec4899"
    }
  ];

  const petInjuries = [
    {
      service: "Abscesses",
      description: "Draining and treating painful infections",
      icon: Target,
      color: "#ef4444"
    },
    {
      service: "Back or Muscle Pain",
      description: "Diagnostics and comfort measures for strains or disc issues",
      icon: Activity,
      color: "#f59e0b"
    },
    {
      service: "Vaccine Reactions",
      description: "Immediate response to adverse symptoms",
      icon: Shield,
      color: "#10b981"
    },
    {
      service: "Bites, Stings & Wounds",
      description: "Cleaning, antibiotics, and pain support to prevent complications",
      icon: Bandage,
      color: "#8b5cf6"
    }
  ];

  const behavioralIssues = [
    {
      service: "Allergic Reactions",
      description: "Treatment for hives, facial swelling, and itching",
      icon: AlertTriangle,
      color: "#dc2626"
    },
    {
      service: "Anal Gland Issues",
      description: "Relief from scooting, licking, and discomfort",
      icon: CircleDot,
      color: "#f59e0b"
    },
    {
      service: "Anxiety & Motion Sickness",
      description: "Calming techniques and temporary support to ease travel stress",
      icon: Heart,
      color: "#10b981"
    },
    {
      service: "Bad Breath or Oral Discomfort",
      description: "Quick oral health checkups and treatment for underlying issues",
      icon: Smile,
      color: "#6366f1"
    }
  ];

  const processSteps = [
    {
      step: "Call or Save Your Spot Online",
      description: "By Appointment Only. Please call us before you walk in so that we can tell you the wait times and help you to come when it is free",
      icon: Calendar,
      number: "01"
    },
    {
      step: "Fast, Gentle Care",
      description: "Escorted directly to a calm, Fear-Free certified exam room where our team listens and acts quickly",
      icon: Heart,
      number: "02"
    },
    {
      step: "Tailored Treatment, Clear Plan",
      description: "Diagnose, treat, and develop a clear plan, then share medical records with your regular vet",
      icon: FileText,
      number: "03"
    }
  ];

  const whyChooseUs = [
    {
      feature: "Quick Access Care",
      description: "Fast appointment scheduling when your pet needs urgent attention",
      icon: Clock
    },
    {
      feature: "Fear-Free Certified Team",
      description: "Minimizing stress for scared or anxious pets",
      icon: Heart
    },
    {
      feature: "Transparent Care",
      description: "Clear pricing, clear plans, and seamless handoff to your primary vet",
      icon: Eye
    },
    {
      feature: "Care Beyond Hours",
      description: "We're here during holidays, weekends, and busy times when your primary vet has no available appointments",
      icon: Calendar
    }
  ];

  const notTreated = [
    "Severe trauma (e.g., hit-by-car incidents)",
    "Surgeries requiring amputation",
    "Overnight hospitalization or intensive care cases"
  ];

  const serviceAreas = [
    "South Riding", "Aldie", "Ashburn", "Chantilly", "Centreville", "Reston", "Herndon"
  ];

  // Hero content for the left side
  const heroContent = (
    <div className="text-center lg:text-left">
      <h1 className="text-xl font-bold text-gray-900 mb-4">
        Urgent Care
      </h1>
      <div className="mb-4">
        <span className="inline-flex items-center px-4 py-2 rounded-md text-sm font-semibold text-white" style={{ backgroundColor: primaryColor }}>
          Immediate Care When Needed
        </span>
      </div>
      <p className="text-base text-gray-600 leading-relaxed">
        When your pet needs urgent attention, our team is ready.
      </p>
    </div>
  );

  return (
    <InnerPageLayout heroContent={heroContent}>
    <div className="bg-gray-50">

      {/* What We Treat Header */}
      <section style={{ backgroundColor: '#f8f9fa', paddingTop: '30px', paddingBottom: '15px' }}>
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8">
            <h2 className="text-xl font-bold text-gray-900 mb-4">
              What We Treat — Fast, Effective, Compassionate Care
            </h2>
            <p className="text-gray-600 max-w-3xl mx-auto">
              Our urgent care services cover a wide range of conditions that need immediate attention but don't 
              require emergency surgery or overnight hospitalization.
            </p>
          </div>

          {/* Pet Illnesses & Discomfort */}
          <div className="mb-12">
            <h3 className="text-lg font-bold text-gray-900 mb-6 text-center">Pet Illnesses & Discomfort</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {petIllnesses.map((illness, index) => (
                <div key={index} className="bg-white p-6 rounded-xl shadow-md hover:shadow-lg transition-shadow">
                  <div className="flex items-center mb-4">
                    <div className="flex items-center justify-center w-12 h-12 rounded-full mr-4" style={{ backgroundColor: `${illness.color}15` }}>
                      <illness.icon className="h-6 w-6" style={{ color: illness.color }} />
                    </div>
                    <h4 className="text-lg font-semibold text-gray-900">{illness.service}</h4>
                  </div>
                  <p className="text-gray-600 text-sm leading-relaxed">{illness.description}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Pet Injuries & Acute Issues */}
          <div className="mb-12">
            <h3 className="text-lg font-bold text-gray-900 mb-6 text-center">Pet Injuries & Acute Issues</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {petInjuries.map((injury, index) => (
                <div key={index} className="bg-white p-6 rounded-xl shadow-md hover:shadow-lg transition-shadow">
                  <div className="flex items-center mb-4">
                    <div className="flex items-center justify-center w-12 h-12 rounded-full mr-4" style={{ backgroundColor: `${injury.color}15` }}>
                      <injury.icon className="h-6 w-6" style={{ color: injury.color }} />
                    </div>
                    <h4 className="text-lg font-semibold text-gray-900">{injury.service}</h4>
                  </div>
                  <p className="text-gray-600 text-sm leading-relaxed">{injury.description}</p>
                </div>
              ))}
            </div>
          </div>

          {/* When Pets Just Don't Seem Right */}
          <div className="mb-12">
            <h3 className="text-lg font-bold text-gray-900 mb-6 text-center">When Pets Just Don't Seem Right</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {behavioralIssues.map((issue, index) => (
                <div key={index} className="bg-white p-6 rounded-xl shadow-md hover:shadow-lg transition-shadow">
                  <div className="flex items-center mb-4">
                    <div className="flex items-center justify-center w-12 h-12 rounded-full mr-4" style={{ backgroundColor: `${issue.color}15` }}>
                      <issue.icon className="h-6 w-6" style={{ color: issue.color }} />
                    </div>
                    <h4 className="text-lg font-semibold text-gray-900">{issue.service}</h4>
                  </div>
                  <p className="text-gray-600 text-sm leading-relaxed">{issue.description}</p>
                </div>
              ))}
            </div>
          </div>

          {/* End-of-Life Support */}
          <div className="mb-12">
            <h3 className="text-lg font-bold text-gray-900 mb-6 text-center">End-of-Life Support</h3>
            <div className="max-w-4xl mx-auto">
              <div className="bg-white p-8 rounded-xl shadow-md">
                <div className="flex items-center mb-4">
                  <div className="flex items-center justify-center w-12 h-12 rounded-full mr-4" style={{ backgroundColor: `${primaryColor}15` }}>
                    <Heart className="h-6 w-6" style={{ color: primaryColor }} />
                  </div>
                  <h4 className="text-xl font-semibold text-gray-900">Compassionate End-of-Life Care</h4>
                </div>
                <p className="text-gray-700 leading-relaxed">
                  We offer peaceful, compassionate end-of-life care when the time comes, ensuring you and your pet 
                  have the dignity and support you both deserve. Our team provides gentle, respectful services during 
                  this difficult time.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* What We Do Not Treat */}
      <section className="bg-white" style={{ paddingTop: '15px', paddingBottom: '15px' }}>
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-xl font-bold text-gray-900 mb-6 text-center">
            What We Do Not Treat
          </h2>
          <div className="max-w-4xl mx-auto">
            <div className="bg-amber-50 p-8 rounded-xl border-l-4 border-amber-500">
              <div className="flex items-center mb-4">
                <AlertTriangle className="h-6 w-6 mr-3 text-amber-600" />
                <h3 className="text-lg font-semibold text-amber-900">Specialized Care Referrals</h3>
              </div>
              <p className="text-amber-800 mb-4">
                While we excel at urgent care, certain conditions are better managed by specialized ER teams or your primary veterinarian:
              </p>
              <div className="space-y-2">
                {notTreated.map((condition, index) => (
                  <div key={index} className="flex items-center">
                    <XCircle className="h-4 w-4 mr-2 text-amber-600 flex-shrink-0" />
                    <span className="text-amber-800">{condition}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section style={{ backgroundColor: '#f8f9fa', paddingTop: '15px', paddingBottom: '15px' }}>
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-xl font-bold text-gray-900 mb-6 text-center">
            How It Works: The Urgent Care Experience
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {processSteps.map((step, index) => (
              <div key={index} className="bg-white p-8 rounded-xl shadow-md text-center relative">
                <div className="absolute top-4 right-4 w-8 h-8 rounded-full text-white text-sm font-bold flex items-center justify-center" style={{ backgroundColor: primaryColor }}>
                  {step.number}
                </div>
                <div className="flex items-center justify-center w-16 h-16 rounded-full mx-auto mb-6" style={{ backgroundColor: primaryBg }}>
                  <step.icon className="h-8 w-8" style={{ color: primaryColor }} />
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-4">{step.step}</h3>
                <p className="text-gray-600 text-sm leading-relaxed">{step.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="bg-white" style={{ paddingTop: '15px', paddingBottom: '15px' }}>
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-xl font-bold text-gray-900 mb-6 text-center">
            Why Choose Pets and Vets for Urgent Care?
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {whyChooseUs.map((feature, index) => (
              <div key={index} className="bg-gray-50 p-6 rounded-xl shadow-md text-center">
                <div className="flex items-center justify-center w-16 h-16 rounded-full mx-auto mb-4" style={{ backgroundColor: primaryBg }}>
                  <feature.icon className="h-8 w-8" style={{ color: primaryColor }} />
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-3">{feature.feature}</h3>
                <p className="text-gray-600 text-sm">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Urgent Care Hours - Dynamic Section */}
      <section style={{ backgroundColor: '#f8f9fa', paddingTop: '15px', paddingBottom: '15px' }}>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-red-50 rounded-xl p-8 border-2 border-red-100">
            <div className="text-center mb-6">
              <h2 className="text-xl font-bold text-red-900 mb-2 flex items-center justify-center">
                <Clock className="h-6 w-6 mr-3 text-red-600" />
                Urgent Care Hours
                {loading && <span className="ml-2 text-sm text-red-500">(Loading...)</span>}
              </h2>
              <p className="text-red-700 text-sm">Available by appointment only - Call ahead to schedule</p>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {[
                { day: 'Monday', key: 'monday' },
                { day: 'Tuesday', key: 'tuesday' },
                { day: 'Wednesday', key: 'wednesday' },
                { day: 'Thursday', key: 'thursday' },
                { day: 'Friday', key: 'friday' },
                { day: 'Saturday', key: 'saturday' },
                { day: 'Sunday', key: 'sunday' }
              ].map((dayInfo, index) => {
                const dayData = urgentCareHours?.[dayInfo.key];
                const defaultHour = { is_open: true, open_time: '15:00', close_time: '22:00' }; // Default fallback
                
                return (
                  <div key={index} className="bg-white p-4 rounded-lg shadow-sm border border-red-200">
                    <div className="text-center">
                      <h3 className="font-semibold text-gray-900 mb-1">{dayInfo.day}</h3>
                      <p className={`text-sm font-medium ${
                        (dayData?.is_open ?? defaultHour.is_open) ? 'text-red-600' : 'text-gray-500'
                      }`}>
                        {loading ? '...' : (dayData ? formatDayHours(dayData) : formatDayHours(defaultHour))}
                      </p>
                    </div>
                  </div>
                );
              })}
            </div>
            
            <div className="mt-6 p-4 bg-red-100 rounded-lg border border-red-200">
              <div className="flex items-start space-x-3">
                <Info className="h-5 w-5 text-red-600 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="text-sm text-red-800 mb-2">
                    <strong>Urgent Care Services:</strong> For non-life-threatening conditions that need prompt attention but can wait for an appointment.
                  </p>
                  <p className="text-sm text-red-700">
                    <strong>Call {currentBusinessInfo.phone}</strong> to schedule your urgent care visit during operating hours.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Service Areas */}
      <section className="bg-white" style={{ paddingTop: '15px', paddingBottom: '30px' }}>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-xl font-bold text-gray-900 mb-6">
            Serving Your Community
          </h2>
          <p className="text-gray-600 mb-6">
            Providing urgent veterinary care across:
          </p>
          <div className="flex flex-wrap justify-center gap-3">
            {serviceAreas.map((area, index) => (
              <span key={index} className="px-4 py-2 rounded-full text-white font-medium" style={{ backgroundColor: primaryColor }}>
                {area}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* Emergency CTA */}
      <section style={{ background: `linear-gradient(135deg, ${urgentColor} 0%, #b91c1c 100%)`, paddingTop: '30px', paddingBottom: '30px' }}>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="flex items-center justify-center mb-6">
            <Siren className="h-8 w-8 mr-3 text-white" />
            <h2 className="font-bold text-white" style={{ fontSize: '1rem' }}>
              Ready to Help Your Pet Feel Better — Right Now
            </h2>
          </div>
          <p className="mb-8 text-white" style={{ fontSize: '1rem' }}>
            If your pet is sick, injured, or just not acting like themselves, we're here to help
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href={`tel:${currentBusinessInfo.phone}`}
              className="inline-flex items-center justify-center bg-white px-8 py-3 rounded-lg font-semibold transition-colors duration-200"
              style={{ color: urgentColor }}
              onMouseEnter={(e) => e.target.style.backgroundColor = '#f9fafb'}
              onMouseLeave={(e) => e.target.style.backgroundColor = 'white'}
            >
              <Phone className="mr-2 h-5 w-5" />
              Call Now: {currentBusinessInfo.phone}
            </a>
            <Link
              to="/urgent-care-booking"
              className="inline-flex items-center justify-center border-2 border-white text-white px-8 py-3 rounded-lg font-semibold transition-colors duration-200"
              onMouseEnter={(e) => {
                e.target.style.backgroundColor = 'white';
                e.target.style.color = urgentColor;
              }}
              onMouseLeave={(e) => {
                e.target.style.backgroundColor = 'transparent';  
                e.target.style.color = 'white';
              }}
            >
              Check In Online
              <Navigation className="ml-2 h-5 w-5" />
            </Link>
          </div>
        </div>
      </section>
    </div>
    </InnerPageLayout>
  );
};

export default UrgentCare;